import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import CareTeamEditor from '../components/CareTeamEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPatientEdit = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [employers, setEmployers] = useState([]);

  // NCR Cities (16 cities within Metro Manila)
  const ncrCities = [
    'Manila', 'Quezon City', 'Makati', 'Pasig', 'Taguig', 'Parañaque', 
    'Muntinlupa', 'Las Piñas', 'Mandaluyong', 'Marikina', 'Pasay', 
    'Caloocan', 'Malabon', 'Navotas', 'Valenzuela', 'San Juan'
  ];

  // All other provinces
  const provinces = [
    'Abra', 'Agusan del Norte', 'Agusan del Sur', 'Aklan', 'Albay', 'Antique',
    'Apayao', 'Aurora', 'Basilan', 'Bataan', 'Batanes', 'Batangas', 'Benguet', 'Biliran',
    'Bohol', 'Bukidnon', 'Bulacan', 'Cagayan', 'Camarines Norte', 'Camarines Sur', 'Camiguin',
    'Capiz', 'Catanduanes', 'Cavite', 'Cebu', 'Cotabato', 'Davao de Oro', 'Davao del Norte',
    'Davao del Sur', 'Davao Occidental', 'Davao Oriental', 'Dinagat Islands', 'Eastern Samar',
    'Guimaras', 'Ifugao', 'Ilocos Norte', 'Ilocos Sur', 'Iloilo', 'Isabela', 'Kalinga',
    'La Union', 'Laguna', 'Lanao del Norte', 'Lanao del Sur', 'Leyte', 'Maguindanao',
    'Marinduque', 'Masbate', 'Misamis Occidental', 'Misamis Oriental', 'Mountain Province',
    'Negros Occidental', 'Negros Oriental', 'Northern Samar', 'Nueva Ecija', 'Nueva Vizcaya',
    'Occidental Mindoro', 'Oriental Mindoro', 'Palawan', 'Pampanga', 'Pangasinan', 'Quezon',
    'Quirino', 'Rizal', 'Romblon', 'Samar', 'Sarangani', 'Siquijor', 'Sorsogon',
    'South Cotabato', 'Southern Leyte', 'Sultan Kudarat', 'Sulu', 'Surigao del Norte',
    'Surigao del Sur', 'Tarlac', 'Tawi-Tawi', 'Zambales', 'Zamboanga del Norte',
    'Zamboanga del Sur', 'Zamboanga Sibugay'
  ];

  // Combine NCR cities and provinces for the location dropdown
  const locationOptions = [
    { label: '--- NCR Cities ---', value: '', disabled: true },
    ...ncrCities.map(city => ({ label: city, value: city })),
    { label: '--- Provinces ---', value: '', disabled: true },
    ...provinces.map(province => ({ label: province, value: province }))
  ];

  useEffect(() => {
    fetchPatient();
    fetchEmployers();
  }, [patientId]);

  const fetchEmployers = async () => {
    try {
      const response = await axios.get(`${API}/employers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEmployers(response.data);
    } catch (error) {
      // console.error('Error fetching employers:', error);
    }
  };

  const fetchPatient = async () => {
    try {
      const response = await axios.get(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatient(response.data);
      setEditedData(response.data);
    } catch (error) {
      // console.error('Error fetching patient:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send identifying_data, membership_status, and care_team (no clinical data)
      const adminUpdates = {
        identifying_data: editedData.identifying_data,
        membership_status: editedData.membership_status,
        care_team: editedData.care_team
      };
      
      await axios.put(
        `${API}/patients/${patientId}`,
        adminUpdates,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Patient data saved successfully');
      await fetchPatient();
    } catch (error) {
      // console.error('Error saving patient:', error);
      toast.error('Failed to save patient data');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleCareTeamChange = (updatedTeam) => {
    setEditedData(prev => ({
      ...prev,
      care_team: updatedTeam
    }));
  };

  const handleEmployerAssignment = async (employerId) => {
    try {
      await axios.put(
        `${API}/admin/patients/${patientId}/assign-employer`,
        { employer_id: employerId || null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(employerId ? 'Employer assigned successfully' : 'Employer removed');
      setEditedData(prev => ({ ...prev, employer_id: employerId || null }));
    } catch (error) {
      // console.error('Error assigning employer:', error);
      toast.error('Failed to assign employer');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      updateField('identifying_data', 'profile_photo', reader.result);
      toast.success('Photo uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to upload photo');
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Patient not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button className="btn-ghost" data-testid="back-button">
                <ArrowLeft size={18} className="mr-2" />
                Back to Census
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{editedData.identifying_data?.full_name}</h1>
              <p className="text-slate-600">Edit Patient Information (Admin View)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              data-testid="save-button"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Identifying Data */}
          <div className="col-span-12 md:col-span-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Patient Information</h2>
              
              <div className="space-y-4">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    {editedData.identifying_data?.profile_photo ? (
                      <img
                        src={editedData.identifying_data.profile_photo}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-4 border-cyan-100"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-cyan-100 border-4 border-cyan-200 flex items-center justify-center">
                        <span className="text-2xl font-bold text-cyan-700">
                          {editedData.identifying_data?.full_name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Profile Photo</Label>
                  <div className="mt-2 space-y-3">
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          id="photo-upload"
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById('photo-upload').click()}
                          className="btn-secondary w-full"
                        >
                          <Upload size={16} className="mr-2" />
                          Upload Photo
                        </Button>
                      </label>
                      {editedData.identifying_data?.profile_photo && (
                        <Button
                          type="button"
                          onClick={() => updateField('identifying_data', 'profile_photo', '')}
                          className="btn-ghost text-red-600"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <div className="relative">
                      <Label className="text-xs text-slate-600">Or paste image URL:</Label>
                      <Input
                        value={editedData.identifying_data?.profile_photo?.startsWith('data:') ? '' : (editedData.identifying_data?.profile_photo || '')}
                        onChange={(e) => updateField('identifying_data', 'profile_photo', e.target.value)}
                        placeholder="https://example.com/photo.jpg"
                        className="mt-1"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Max file size: 2MB. Supported: JPG, PNG, GIF</p>
                  </div>
                </div>
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={editedData.identifying_data?.full_name || ''}
                    onChange={(e) => updateField('identifying_data', 'full_name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Nickname</Label>
                  <Input
                    value={editedData.identifying_data?.nickname || ''}
                    onChange={(e) => updateField('identifying_data', 'nickname', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Birthdate</Label>
                  <Input
                    type="date"
                    value={editedData.identifying_data?.birthdate || ''}
                    onChange={(e) => updateField('identifying_data', 'birthdate', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Sex Assigned at Birth</Label>
                  <select
                    value={editedData.identifying_data?.sex || ''}
                    onChange={(e) => updateField('identifying_data', 'sex', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Intersex">Intersex</option>
                  </select>
                </div>
                <div>
                  <Label>Pronouns</Label>
                  <select
                    value={editedData.identifying_data?.pronouns || ''}
                    onChange={(e) => updateField('identifying_data', 'pronouns', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="">Select...</option>
                    <option value="he/him">he/him</option>
                    <option value="she/her">she/her</option>
                    <option value="they/them">they/them</option>
                  </select>
                </div>
                <div>
                  <Label>Birthplace</Label>
                  <Input
                    value={editedData.identifying_data?.birthplace || ''}
                    onChange={(e) => updateField('identifying_data', 'birthplace', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Location (NCR City or Province)</Label>
                  <select
                    value={editedData.identifying_data?.location || ''}
                    onChange={(e) => updateField('identifying_data', 'location', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="">Select Location...</option>
                    {locationOptions.map((option, index) => (
                      <option 
                        key={option.value || `loc-${index}`} 
                        value={option.value} 
                        disabled={option.disabled}
                        className={option.disabled ? 'font-semibold text-slate-400 bg-slate-100' : ''}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Home Address</Label>
                  <Input
                    value={editedData.identifying_data?.home_address || ''}
                    onChange={(e) => updateField('identifying_data', 'home_address', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Occupation</Label>
                  <Input
                    value={editedData.identifying_data?.occupation || ''}
                    onChange={(e) => updateField('identifying_data', 'occupation', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Employer</Label>
                  <Input
                    value={editedData.identifying_data?.employer_name || ''}
                    onChange={(e) => updateField('identifying_data', 'employer_name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Work Address</Label>
                  <Input
                    value={editedData.identifying_data?.work_address || ''}
                    onChange={(e) => updateField('identifying_data', 'work_address', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Emergency Contact</h3>
                  <div className="space-y-3">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editedData.identifying_data?.emergency_contact_name || ''}
                        onChange={(e) => updateField('identifying_data', 'emergency_contact_name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input
                        value={editedData.identifying_data?.emergency_contact_relationship || ''}
                        onChange={(e) => updateField('identifying_data', 'emergency_contact_relationship', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editedData.identifying_data?.emergency_contact_phone || ''}
                        onChange={(e) => updateField('identifying_data', 'emergency_contact_phone', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-span-12 md:col-span-6 space-y-6">
            {/* Membership Status */}
            <div className="card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Membership Status</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <select
                    value={editedData.membership_status?.status || ''}
                    onChange={(e) => updateField('membership_status', 'status', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expiring_soon">Expiring Soon</option>
                  </select>
                </div>
                <div>
                  <Label>Onboarding Status</Label>
                  <select
                    value={editedData.membership_status?.onboarding_status || ''}
                    onChange={(e) => updateField('membership_status', 'onboarding_status', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editedData.membership_status?.start_date || ''}
                    onChange={(e) => updateField('membership_status', 'start_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={editedData.membership_status?.expiry_date || ''}
                    onChange={(e) => updateField('membership_status', 'expiry_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Health Interview Status</Label>
                  <select
                    value={editedData.membership_status?.health_interview_status || ''}
                    onChange={(e) => updateField('membership_status', 'health_interview_status', e.target.value)}
                    className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <Label>Interview Date</Label>
                  <Input
                    type="date"
                    value={editedData.membership_status?.health_interview_date || ''}
                    onChange={(e) => updateField('membership_status', 'health_interview_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Care Team */}
            <div className="card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Care Team Assignment</h2>
              <CareTeamEditor 
                careTeam={editedData.care_team || []} 
                onChange={handleCareTeamChange}
              />
            </div>

            {/* Employer Assignment */}
            <div className="card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Employer Assignment</h2>
              <div>
                <Label>Assign to Employer</Label>
                <select
                  value={editedData.employer_id || ''}
                  onChange={(e) => handleEmployerAssignment(e.target.value)}
                  className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  data-testid="employer-assignment-select"
                >
                  <option value="">No employer assigned</option>
                  {employers.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.company_name || `${emp.first_name} ${emp.last_name}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-2">Assign this patient to an employer account. The employer will be able to see enrollment status only.</p>
              </div>
            </div>

            {/* Notice about clinical data */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Admin Access Note:</strong> Clinical data, health goals, and health records are not accessible from the admin view. 
                Only providers can view and edit clinical information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPatientEdit;
